import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { clientApi } from "@/services/api";
import { Card, Input, StatusBadge, Spinner, Pagination } from "@/components/ui";

export default function ClientBudgetsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["client-budgets", search, page],
    queryFn: () =>
      clientApi.listBudgets({
        search: search || undefined,
        page,
        per_page: 10,
      }),
  });

  const budgets = data?.data?.items || [];
  const total = data?.data?.total || 0;
  const perPage = data?.data?.per_page || 10;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            Meus Orçamentos
          </h1>
          <p className="text-text-secondary">
            Acompanhe e responda seus orçamentos
          </p>
        </div>
        <div className="w-full sm:w-80">
          <Input
            placeholder="Buscar por número ou título"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="p-6 flex justify-center">
            <Spinner size="lg" />
          </div>
        ) : budgets.length === 0 ? (
          <div className="p-6 text-center text-text-secondary">
            Nenhum orçamento encontrado.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="py-3 px-4 text-text-muted font-medium">
                    Número
                  </th>
                  <th className="py-3 px-4 text-text-muted font-medium">
                    Título
                  </th>
                  <th className="py-3 px-4 text-text-muted font-medium">
                    Status
                  </th>
                  <th className="py-3 px-4 text-text-muted font-medium">
                    Valor
                  </th>
                </tr>
              </thead>
              <tbody>
                {budgets.map((budget: any) => (
                  <tr
                    key={budget.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <Link
                        to={`/client/budgets/${budget.id}`}
                        className="text-accent-primary hover:underline font-medium"
                      >
                        {budget.number}
                      </Link>
                    </td>
                    <td className="py-3 px-4 text-text-secondary">
                      {budget.title || "Sem título"}
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={budget.status} />
                    </td>
                    <td className="py-3 px-4 text-text-secondary">
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(Number(budget.total) || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {total > perPage && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={total}
          itemsPerPage={perPage}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
